﻿import React, { useState, useRef, useEffect, useCallback, useMemo, startTransition } from 'react';

import {
    SubtitleEditorList,
    buildSubtitleHighlightIndex,
    findSubtitleHighlightMatch,
    buildTemporarySubtitle,
    getLocalInsertBlockReason,
    mapCreatedSubtitleToLegacy,
    normalizeLocalInsertIndex,
    replaceTempOrAppendSubtitle,
    resolveCreateSubtitleAnchorId,
    spliceSubtitleAfterIndex,
} from '.';
import type { AddSubtitleDraftParams } from '.';

import {
    updateSubtitleApi,
    createSubtitleApi,
    retranslateSubtitleApi,
    audioRegenerateApi,
    changeSubtitleSpeakerApi,
    batchChangeSubtitleSpeakerApi,
    adjustVolumeAndSpeedApi,
    adjustTimestampRangeApi,
    updatePostTextApi,
    getSubtitleByIdApi,
} from '../../../service/subtitles';
import {
    addVideoSpeakerApi,
    getSpeakersByTaskApi,
    renameVideoSpeakerApi,
    getSeriesSpeakersApi,
    promoteSpeakerApi,
    deleteSpeakerApi,
} from '../../../service/speakers';
import {
    isPostTextEqualIgnoringMarkers,
    isTargetTextUnchangedAfterStrippingUserBreaks,
    normalizeTargetPostTextFromEditor,
    stripTargetTextUserLineBreaks,
} from '../../../utils/subtitlePostText';
import { getSubtitlesListApi } from '../../../service/task';
import { usePlaybackStore } from '../videoTimeBox/playbackStore';
import { processTransAudioUrl } from '../../../utils/videoUrl';
import { audioManager } from '../../../utils/audioManager';

/** 修改原文后废弃该行目标音频：清预取缓存并停止 AudioManager 实例 */
const evictTargetTransAudioCache = (subtitleId: number, transAudioPath?: string | null) => {
    if (transAudioPath?.trim()) {
        const url = processTransAudioUrl(transAudioPath);
        const map = window.prefetchedTransAudios;
        const cached = map?.get(url);
        if (cached) {
            try {
                cached.pause();
                cached.removeAttribute('src');
                cached.load();
            } catch {
                /* ignore */
            }
            map?.delete(url);
        }
    }
    try {
        const audioId = `target_audio_${subtitleId}`;
        if (audioManager.isPlaying(audioId)) {
            audioManager.stopSound(audioId);
        }
        audioManager.destroySound(audioId);
    } catch {
        /* ignore */
    }
};

export interface TextListControllerParams {
    videoData: Task.GetTaskStatusResult | null;
    setVideoData: React.Dispatch<React.SetStateAction<Task.GetTaskStatusResult | null>>;
    setVideoDataSafe: (data: Task.GetTaskStatusResult | null) => void;
    id: string | undefined;
    seriesId: string | null;
    urlLangCode: string | null;
    isFromPure: boolean;
    showError: (msg: string) => void;
    showSuccess: (msg: string) => void;
    showWarning: (msg: string) => void;
    timelineEditorRef: React.MutableRefObject<any>;
    pendingTimelineUpdates: Map<number, { audio_speed: number; post_start_time: string; post_end_time: string }>;
    targetText: Array<{ id: number; text: string; timeRange: string; [key: string]: any }>;
    setTargetText: React.Dispatch<React.SetStateAction<Array<{ id: number; text: string; timeRange: string; [key: string]: any }>>>;
}

export const useTextListController = ({
    videoData,
    setVideoData,
    setVideoDataSafe,
    id,
    seriesId,
    urlLangCode,
    isFromPure,
    showError,
    showSuccess,
    showWarning,
    timelineEditorRef,
    pendingTimelineUpdates,
    targetText,
    setTargetText,
}: TextListControllerParams) => {
    const [updatingSubtitles, setUpdatingSubtitles] = useState<{ [key: number]: boolean }>({});
    const [subtitleUpdateErrors, setSubtitleUpdateErrors] = useState<{ [key: number]: string }>({});

    const [openHistorySubtitleId, setOpenHistorySubtitleId] = useState<number | null>(null);

    const [volumeMap, setVolumeMap] = useState<Map<number, number>>(new Map());
    
    // 倍速映射表，存储每个字幕的最新倍速设置
    const [speedMap, setSpeedMap] = useState<Map<number, number>>(new Map());
    
    // 更新音量映射表的函数
    const handleUpdateVolume = useCallback((subtitleId: number, volume: number) => {
        setVolumeMap(prev => {
            const newMap = new Map(prev);
            newMap.set(subtitleId, volume);
            return newMap;
        });
        // 更新音量映射表
    }, []);

    // 更新倍速映射表的函数
    const handleUpdateSpeed = useCallback((subtitleId: number, speed: number) => {
        setSpeedMap(prev => {
            const newMap = new Map(prev);
            newMap.set(subtitleId, speed);
            return newMap;
        });
        // 更新倍速映射表
    }, []);

    // 处理历史记录面板的打开/关闭 - 确保只显示一个
    const handleOpenHistoryPanel = useCallback((subtitleId: number) => {
        setOpenHistorySubtitleId(subtitleId);
    }, []);

    const handleCloseHistoryPanel = useCallback(() => {
        setOpenHistorySubtitleId(null);
    }, []);

    const [rowSelectedRoles, setRowSelectedRoles] = useState<{ [key: number]: string }>({});
    
    // 剧集音色库标记状态管理 - 记录哪些台词已添加到剧集音色库
    const [seriesVoiceLibraryMarkers, setSeriesVoiceLibraryMarkers] = useState<{ [key: number]: boolean }>({});
    // 从字幕数据中提取说话人信息
    const [roles, setRoles] = useState<Array<{ key: string; label: string; speakCount?: number; isSeriesSpeaker?: boolean }>>([]);
    
    // 剧集说话人角色数据（带说话人ID）
    const [seriesSpeakers, setSeriesSpeakers] = useState<Array<{ id: number; key: string; label: string; description: string }>>([]);

    // 角色编辑状态管理
    const [editingRoleKey, setEditingRoleKey] = useState<string | null>(null);
    const [editingRoleName, setEditingRoleName] = useState('');
    
    // 防重复调用状态
    const [isSavingRole, setIsSavingRole] = useState(false);

    // 从字幕数据中提取说话人信息（备用方案）
    const extractSpeakersFromSubtitles = (subtitles: Task.SubtitlesLegacy[]) => {
        const speakerMap = new Map<string, { key: string; label: string }>();
        
        subtitles.forEach(subtitle => {
            if (subtitle.speakers && subtitle.speakers.speaker_name) {
                const speakerName = subtitle.speakers.speaker_name;
                // 使用说话人名称作为唯一标识，避免重复
                if (!speakerMap.has(speakerName)) {
                    speakerMap.set(speakerName, {
                        key: `speaker_${speakerName}`, // 使用说话人名称作为key的一部分
                        label: speakerName
                    });
                }
            }
        });
        
        // 转换为角色数组格式
        const speakerRoles = Array.from(speakerMap.values());
        
        // 从字幕数据提取的说话人列表（已去重）
        return speakerRoles;
    };

    const [isFetchingSeriesSpeakers, setIsFetchingSeriesSpeakers] = useState(false);
    const [isPromotingSpeaker, setIsPromotingSpeaker] = useState(false);
    /** 说话人列表请求序号：新请求递增，用于丢弃「后发先至」的过期响应 */
    const speakersFetchSeqRef = useRef(0);

    // 去重函数：确保roles数组中没有重复的角色
    const deduplicateRoles = (roles: Array<{ key: string; label: string }>) => {
        const roleMap = new Map<string, { key: string; label: string }>();
        
        roles.forEach(role => {
            // 使用label作为唯一标识，如果已存在则保留第一个
            if (!roleMap.has(role.label)) {
                roleMap.set(role.label, role);
            } else {
                // 发现重复的角色名称，已跳过
            }
        });
        
        const uniqueRoles = Array.from(roleMap.values());
        if (uniqueRoles.length !== roles.length) {
            // 角色去重完成
        }
        
        return uniqueRoles;
    };

    // 获取说话人列表（getSpeakersByTaskApi 已在 service 层规范为 { success, data: Speaker[] }，此处不再做多形态容错）
    const fetchSpeakersList = async (
        taskId: number,
        taskMeta?: { series_id?: number | null; seriesId?: number | null; dubbingMethod?: number | null }
    ) => {
        const seq = ++speakersFetchSeqRef.current;

        try {
            const effectiveDubbingMethod = taskMeta?.dubbingMethod ?? videoData?.dubbingMethod ?? null;
            const effectiveSeriesId = Number(seriesId || taskMeta?.series_id || taskMeta?.seriesId || videoData?.series_id);
            const shouldFetchSeriesOnly = effectiveDubbingMethod === 2 && Number.isFinite(effectiveSeriesId) && effectiveSeriesId > 0;
            const { data: speakersData } = shouldFetchSeriesOnly
                ? await getSpeakersByTaskApi(undefined, effectiveSeriesId)
                : await getSpeakersByTaskApi(taskId);

            if (seq !== speakersFetchSeqRef.current) {
                return [];
            }

            if (!speakersData.length) {
                return [];
            }

            const finalRoles = speakersData
                .filter((speaker) => speaker.speaker_name)
                .map((speaker) => ({
                    key: `speaker_${speaker.id}`,
                    label: speaker.speaker_name,
                    speakCount: speaker.speak_count ?? undefined,
                    isSeriesSpeaker: speaker.is_series_speaker,
                }));

            setRoles(finalRoles);
            return finalRoles;
        } catch (error: any) {
            console.error('❌ 获取说话人列表出错:', error);
            return [];
        }
    };

    // 获取剧集说话人角色列表
    const fetchSeriesSpeakersList = async (seriesId: number, langCode?: string) => {
        // 防止重复调用
        if (isFetchingSeriesSpeakers) {
            // 获取剧集说话人角色列表，跳过重复调用
            return [];
        }

        // langCode 缺失时不调用接口
        if (!langCode) {
            return [];
        }

        try {
            setIsFetchingSeriesSpeakers(true);
            // 调用 getSeriesSpeakersApi
            const response = await getSeriesSpeakersApi(seriesId, langCode);
            
            // 处理API响应数据
            let speakersData = null;
            
            // 情况1: 标准格式 {success: true, data: [...]}
            if (response && typeof response === 'object' && 'success' in response && response.success && response.data && Array.isArray(response.data)) {
                speakersData = response.data;
            }
            // 情况2: 直接返回数组格式 [...] (根据您提供的示例数据)
            else if (Array.isArray(response)) {
                speakersData = response;
            }
            
            if (speakersData && Array.isArray(speakersData) && speakersData.length > 0) {
                // 将API返回的说话人数据转换为角色数组格式（带id）
                const speakerRoles = speakersData.map((speaker: any) => ({
                    id: speaker.id,
                    key: `series_speaker_${speaker.id}`,
                    label: speaker.speaker_name,
                    description: speaker.description || ''
                }));
                
                setSeriesSpeakers(speakerRoles);
                
                // 🍎 进入页面时，将获取的角色列表备份到 localStorage
                const storageKey = `seriesSpeakers_${seriesId}_${langCode}`;
                try {
                    localStorage.setItem(storageKey, JSON.stringify(speakerRoles));
                    console.log('🍎 角色列表已备份到本地:', speakerRoles);
                } catch (error) {
                    console.error('🍎 备份角色列表到本地失败:', error);
                }
                
                return speakerRoles;
            } else {
                // 即使为空数组，也保存到本地
                const storageKey = `seriesSpeakers_${seriesId}_${langCode}`;
                try {
                    localStorage.setItem(storageKey, JSON.stringify([]));
                    console.log('🍎 角色列表已备份到本地（空列表）');
                } catch (error) {
                    console.error('🍎 备份角色列表到本地失败:', error);
                }
                return [];
            }
        } catch (error: any) {
            return [];
        } finally {
            setIsFetchingSeriesSpeakers(false);
        }
    };

    useEffect(() => {
        // 只有在已经拿到 langCode 时才请求，避免先发一次缺少 langCode 的请求
        const langCodeToUse = videoData?.langCode || videoData?.lang_code;
        if (!langCodeToUse) {
            return;
        }

        // 优先使用 URL 上的 seriesId，其次使用视频数据里的 series_id
        const rawSeriesId = seriesId || (videoData?.series_id != null ? String(videoData.series_id) : null);
        if (!rawSeriesId) {
            return;
        }
        const parsedId = parseInt(rawSeriesId, 10);
        if (Number.isNaN(parsedId)) {
            return;
        }
        
        // 视频编辑页初始化调用 fetchSeriesSpeakersList
        fetchSeriesSpeakersList(parsedId, langCodeToUse);
    }, [seriesId, videoData?.series_id, videoData?.langCode, videoData?.lang_code]);

    const handlePromoteToSeriesSpeaker = useCallback(async (speakerId: number, subtitleId: number): Promise<void> => {
        if (isPromotingSpeaker) {
            return;
        }

        try {
            setIsPromotingSpeaker(true);
            const response = await promoteSpeakerApi(speakerId, subtitleId);

            if (response.code === 0) {
                showSuccess('已添加到剧集音色库');
                // 必须写回 videoData：否则列表仍用旧的 speakers.id，按 id 统计的 (N) 与「剧集音色库」状态不一致
                try {
                    const detail = await getSubtitleByIdApi(subtitleId);
                    setVideoData(prev => {
                        if (!prev?.subtitles) return prev;
                        const nextSpeakers = detail.speakers;
                        const updatedSubtitles = prev.subtitles.map(sub =>
                            sub.id === subtitleId
                                ? {
                                      ...sub,
                                      speakers: {
                                          ...sub.speakers,
                                          id: nextSpeakers.id,
                                          series_id: nextSpeakers.series_id,
                                          task_id: nextSpeakers.task_id,
                                          speaker_name: nextSpeakers.speaker_name,
                                          description: nextSpeakers.description,
                                          is_series_speaker: nextSpeakers.is_series_speaker,
                                      },
                                  }
                                : sub
                        );
                        return { ...prev, subtitles: updatedSubtitles };
                    });
                } catch (syncErr) {
                    console.error('添加到剧集音色库后同步字幕说话人失败:', syncErr);
                }
            } else {
                // 业务失败也抛出错误，让调用方（RowItem）走 catch 分支，不更新本地说话人
                const msg = response.msg || '添加到剧集音色库失败';
                showError(msg);
                throw new Error(msg);
            }
        } catch (error: any) {
            // request 封装在业务错误时会把 msg/message 填到 error.message，这里直接展示
            showError(error?.message || '添加到剧集音色库失败，请重试');
            // 向上传递错误，阻止调用方执行“本地成功更新”逻辑
            throw error;
        } finally {
            setIsPromotingSpeaker(false);
        }
    }, [isPromotingSpeaker, showSuccess, showError, setVideoData]);

    // 删除字幕处理函数
    const handleDeleteSubtitle = useCallback((subtitleId: number) => {
        // 删除字幕
        
        // 从videoData中移除对应的字幕
        if (videoData && videoData.subtitles) {
            const updatedSubtitles = videoData.subtitles.filter(subtitle => subtitle.id !== subtitleId);
            setVideoDataSafe({
                ...videoData,
                subtitles: updatedSubtitles
            });
        }
        
        // 从角色选择状态中移除对应的记录
        setRowSelectedRoles(prev => {
            const newRoles = { ...prev };
            delete newRoles[subtitleId];
            return newRoles;
        });
        
        // 从剧集音色库标记中移除对应的记录
        setSeriesVoiceLibraryMarkers(prev => {
            const newMarkers = { ...prev };
            delete newMarkers[subtitleId];
            return newMarkers;
        });
        
        // 从更新状态中移除对应的记录
        setUpdatingSubtitles(prev => {
            const newUpdating = { ...prev };
            delete newUpdating[subtitleId];
            return newUpdating;
        });
        
        // 从错误状态中移除对应的记录
        setSubtitleUpdateErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[subtitleId];
            return newErrors;
        });
        
        showSuccess('字幕删除成功');
    }, [videoData, setVideoDataSafe, showSuccess]);
    // 新增字幕处理函数（编排：本地临时插入 / 远端创建 + 持久化 post 时间）
    const handleAddSubtitle = useCallback(
        async (
            newSubtitleParams: AddSubtitleDraftParams,
            isLocalInsert: boolean = false,
            currentSubtitleId?: number
        ) => {
            if (!videoData?.subtitles) {
                return;
            }

            if (isLocalInsert) {
                let rawInsertIndex = -1;
                let currentSubtitle: Task.SubtitlesLegacy | null = null;
                if (currentSubtitleId !== undefined) {
                    rawInsertIndex = videoData.subtitles.findIndex((sub) => sub.id === currentSubtitleId);
                    currentSubtitle =
                        rawInsertIndex >= 0 ? videoData.subtitles[rawInsertIndex] : null;
                }

                const blockReason = getLocalInsertBlockReason(
                    videoData.subtitles,
                    newSubtitleParams,
                    rawInsertIndex
                );
                if (blockReason) {
                    showWarning(blockReason);
                    return;
                }

                const tempId = -(Date.now() + Math.random() * 1000);
                const newSubtitle = buildTemporarySubtitle(
                    videoData.id,
                    newSubtitleParams,
                    tempId,
                    currentSubtitle
                );

                const normalizedInsertIndex = normalizeLocalInsertIndex(videoData.subtitles, rawInsertIndex);
                const updatedSubtitles = spliceSubtitleAfterIndex(
                    videoData.subtitles,
                    normalizedInsertIndex,
                    newSubtitle
                );

                setVideoDataSafe({
                    ...videoData,
                    subtitles: updatedSubtitles,
                });
                return;
            }

            try {
                const anchor = resolveCreateSubtitleAnchorId(videoData.subtitles, currentSubtitleId);
                if ('error' in anchor) {
                    throw new Error(anchor.error);
                }

                const params: Subtitles.CreateSubtitleParams = {
                    subtitleId: anchor.anchorId,
                    originText: newSubtitleParams.origin_text,
                };

                const response = await createSubtitleApi(params);
                if (response.code !== 0 || !response.data) {
                    throw new Error(response.msg || '创建字幕失败');
                }

                const newSubtitle = mapCreatedSubtitleToLegacy(response.data, newSubtitleParams);

                if (
                    !isFromPure &&
                    newSubtitle.id > 0 &&
                    newSubtitleParams.post_start_time &&
                    newSubtitleParams.post_end_time
                ) {
                    try {
                        const postStartTimeForApi = newSubtitleParams.post_start_time.replace(',', '.');
                        const postEndTimeForApi = newSubtitleParams.post_end_time.replace(',', '.');

                        await adjustTimestampRangeApi({
                            id: newSubtitle.id,
                            postStartTime: postStartTimeForApi,
                            postEndTime: postEndTimeForApi,
                        });
                    } catch (e) {
                        console.error('创建字幕后持久化 post 时间戳失败:', e);
                    }
                }

                const updatedSubtitles = replaceTempOrAppendSubtitle(
                    videoData.subtitles,
                    newSubtitle,
                    newSubtitleParams
                );

                setVideoDataSafe({
                    ...videoData,
                    subtitles: updatedSubtitles,
                });

                showSuccess('字幕新增成功');
            } catch (error: any) {
                console.error('新增字幕失败:', error);
                showError(error?.message || '新增字幕失败');
            }
        },
        [videoData, setVideoDataSafe, showSuccess, showError, showWarning, isFromPure]
    );

    const handleSubtitleUpdate = useCallback((originalSubtitle: Subtitles.GetSubtitleResponse, newSubtitle: Subtitles.GetSubtitleResponse | null) => {
        // 在进行任何基于 videoData 的更新前，先同步待更新的时间轴数据
        // 场景：用户拖动台词后未播放，直接执行重新配音/合并等操作，如果不先同步，
        // 将会使用旧的字幕时间戳，从而把拖动后的位置「拉回去」
        if (timelineEditorRef.current && pendingTimelineUpdates.size > 0) {
            timelineEditorRef.current.syncPendingUpdatesToVideoData(pendingTimelineUpdates);
        }
        
        if (videoData && videoData.subtitles) {
            // 对于合并操作，我们需要找到当前点击的字幕（第一个字幕）
            // 因为合并API返回的是合并后的新字幕，但我们需要更新的是当前点击的那个字幕
            let originalIndex = -1;
            
            if (newSubtitle === null) {
                // 可能是合并操作或重新配音操作
                const currentSubtitleId = (originalSubtitle as any).currentSubtitleId;
                if (currentSubtitleId) {
                    // 合并操作：使用传递的当前字幕ID
                    originalIndex = videoData.subtitles.findIndex(sub => sub.id === currentSubtitleId);
                } else {
                    // 重新配音操作：使用originalSubtitle的id
                    originalIndex = videoData.subtitles.findIndex(sub => sub.id === originalSubtitle.id);
                }
            } else {
                // 拆分操作或更换音色操作：使用传递的原始字幕ID来查找
                const originalSubtitleId = (originalSubtitle as any).originalSubtitleId;
                if (originalSubtitleId) {
                    originalIndex = videoData.subtitles.findIndex(sub => sub.id === originalSubtitleId);
                } else if (originalSubtitle.id) {
                    // 更换音色操作：使用originalSubtitle.id来查找
                    originalIndex = videoData.subtitles.findIndex(sub => sub.id === originalSubtitle.id);
                } else {
                    originalIndex = -1;
                }
            }
            
            if (originalIndex !== -1) {
                const updatedSubtitles = [...videoData.subtitles];
                
                if (newSubtitle === null) {
                    // 判断是合并操作还是重新配音操作
                    const currentSubtitleId = (originalSubtitle as any).currentSubtitleId;
                    
                    if (currentSubtitleId) {
                        // 合并操作：只更新原始字幕，删除下一个字幕
                        // 更新原始字幕，使用合并后的数据，但保留原有的原始音频和音量数据（如果 API 返回空值）
                        const currentSubtitle = updatedSubtitles[originalIndex];
                        updatedSubtitles[originalIndex] = {
                            ...currentSubtitle,
                            id: originalSubtitle.id, // 使用合并后的新ID
                            origin_text: originalSubtitle.origin_text || currentSubtitle.origin_text,
                            // 合并操作后，清空 post_text，因为需要重新翻译合并后的原文
                            post_text: '',
                            post_start_time: originalSubtitle.post_start_time || currentSubtitle.post_start_time,
                            post_end_time: originalSubtitle.post_end_time || currentSubtitle.post_end_time,
                            origin_start_time: originalSubtitle.origin_start_time || currentSubtitle.origin_start_time,
                            origin_end_time: originalSubtitle.origin_end_time || currentSubtitle.origin_end_time,
                            words: originalSubtitle.words || currentSubtitle.words || [],
                            // 更新其他可能变化的字段（只更新非空值）
                            task_id: originalSubtitle.task_id || currentSubtitle.task_id,
                            is_episode_voice: originalSubtitle.is_episode_voice !== undefined ? originalSubtitle.is_episode_voice : currentSubtitle.is_episode_voice,
                            // 保留原有的原始音频数据，除非 API 明确返回新值
                            origin_audio: originalSubtitle.origin_audio || currentSubtitle.origin_audio,
                            // 合并操作后，清空 trans_audio，因为需要重新生成翻译音频
                            trans_audio: '',
                            subtitle_id: originalSubtitle.subtitle_id || currentSubtitle.subtitle_id,
                            // 保留原有的语速和音量数据
                            audio_speed: originalSubtitle.audio_speed || currentSubtitle.audio_speed || 1.0,
                            volume_gain_db: (originalSubtitle.volume_gain_db !== undefined && originalSubtitle.volume_gain_db !== null && originalSubtitle.volume_gain_db !== 0) 
                                ? originalSubtitle.volume_gain_db 
                                : currentSubtitle.volume_gain_db,
                            speakers: originalSubtitle.speakers || currentSubtitle.speakers,
                            voice_info: originalSubtitle.voice_info || currentSubtitle.voice_info
                        };
                        
                        // 删除下一个字幕（如果存在）
                        if (originalIndex + 1 < updatedSubtitles.length) {
                            updatedSubtitles.splice(originalIndex + 1, 1);
                        }
                    } else {
                        // 判断是重新翻译还是重新配音
                        const currentSubtitle = updatedSubtitles[originalIndex];
                        const isRetranslate = originalSubtitle.post_text && 
                            originalSubtitle.post_text !== currentSubtitle.post_text;
                        
                        if (isRetranslate) {
                            // 重新翻译操作：更新翻译文本和时间戳，并将 trans_audio 置为空
                            // 处理重新翻译操作，更新翻译文本和时间戳，清空 trans_audio
                            updatedSubtitles[originalIndex] = {
                                ...currentSubtitle,
                                post_text: originalSubtitle.post_text || currentSubtitle.post_text || '',
                                post_start_time: originalSubtitle.post_start_time || currentSubtitle.post_start_time,
                                post_end_time: originalSubtitle.post_end_time || currentSubtitle.post_end_time,
                                origin_text: originalSubtitle.origin_text || currentSubtitle.origin_text,
                                origin_start_time: originalSubtitle.origin_start_time || currentSubtitle.origin_start_time,
                                origin_end_time: originalSubtitle.origin_end_time || currentSubtitle.origin_end_time,
                                words: originalSubtitle.words || currentSubtitle.words || [],
                                // 重新翻译后，清空 trans_audio，因为需要重新生成翻译音频
                                trans_audio: '',
                                // 更新其他可能变化的字段
                                speakers: originalSubtitle.speakers || currentSubtitle.speakers,
                                voice_info: originalSubtitle.voice_info || currentSubtitle.voice_info
                            };
                        } else {
                            // 重新配音操作：更新当前台词的配音相关字段和时间戳
                            // 处理重新配音操作，更新当前字幕的音频相关数据和时间戳
                            updatedSubtitles[originalIndex] = {
                                ...currentSubtitle,
                                // 更新目标台词音频和可能的 voice_info/speakers 等配音相关字段
                                trans_audio: originalSubtitle.trans_audio || currentSubtitle.trans_audio,
                                speakers: originalSubtitle.speakers || currentSubtitle.speakers,
                                voice_info: originalSubtitle.voice_info || currentSubtitle.voice_info,
                                // 重新配音后端把 audio_speed 重置为 1.0：用响应里的新值覆盖，
                                // 否则 ...currentSubtitle.audio_speed 会把旧倍速残留到 videoData.subtitles[i]
                                // （与合并字幕分支 line 619 的写法对齐）
                                audio_speed: originalSubtitle.audio_speed ?? currentSubtitle.audio_speed ?? 1.0,
                                // 同步API返回的时间戳（如果存在）
                                post_start_time: originalSubtitle.post_start_time || currentSubtitle.post_start_time,
                                post_end_time: originalSubtitle.post_end_time || currentSubtitle.post_end_time,
                                origin_start_time: originalSubtitle.origin_start_time || currentSubtitle.origin_start_time,
                                origin_end_time: originalSubtitle.origin_end_time || currentSubtitle.origin_end_time
                            };
                        }
                    }
                } else {
                    // 拆分操作或更换音色操作
                    // 判断是否是拆分操作（通过检查newSubtitle.id是否与originalSubtitle.id不同）
                    const isSplitOperation = newSubtitle.id !== originalSubtitle.id;
                    
                    if (isSplitOperation) {
                        // 拆分操作：更新原始字幕并插入新字幕
                        
                        // 直接使用API返回的完整数据替换原始字幕
                        updatedSubtitles[originalIndex] = {
                            id: originalSubtitle.id,
                            task_id: originalSubtitle.task_id,
                            origin_text: originalSubtitle.origin_text,
                            post_text: originalSubtitle.post_text || '',
                            post_start_time: originalSubtitle.post_start_time,
                            post_end_time: originalSubtitle.post_end_time,
                            origin_start_time: originalSubtitle.origin_start_time,
                            origin_end_time: originalSubtitle.origin_end_time,
                            is_episode_voice: originalSubtitle.is_episode_voice,
                            origin_audio: originalSubtitle.origin_audio,
                            trans_audio: originalSubtitle.trans_audio || '',
                            subtitle_id: originalSubtitle.subtitle_id,
                            audio_speed: originalSubtitle.audio_speed,
                            volume_gain_db: originalSubtitle.volume_gain_db,
                            speakers: originalSubtitle.speakers,
                            voice_info: originalSubtitle.voice_info,
                            words: originalSubtitle.words || []
                        };
                        
                        // 在原始字幕后面插入新字幕
                        const newSubtitleData: Task.SubtitlesLegacy = {
                            id: newSubtitle.id,
                            task_id: newSubtitle.task_id,
                            origin_text: newSubtitle.origin_text || '',
                            post_text: newSubtitle.post_text || '',
                            post_start_time: newSubtitle.post_start_time,
                            post_end_time: newSubtitle.post_end_time,
                            origin_start_time: newSubtitle.origin_start_time,
                            origin_end_time: newSubtitle.origin_end_time,
                            is_episode_voice: newSubtitle.is_episode_voice,
                            origin_audio: newSubtitle.origin_audio,
                            trans_audio: newSubtitle.trans_audio || '',
                            subtitle_id: newSubtitle.subtitle_id,
                            audio_speed: newSubtitle.audio_speed,
                            volume_gain_db: newSubtitle.volume_gain_db,
                            speakers: newSubtitle.speakers,
                            voice_info: newSubtitle.voice_info,
                            words: newSubtitle.words || []
                        };
                        
                        // 在原始字幕后面插入新字幕
                        updatedSubtitles.splice(originalIndex + 1, 0, newSubtitleData);
                    } else {
                        // 更换音色操作：直接使用newSubtitle替换原始字幕
                        const currentSubtitle = updatedSubtitles[originalIndex];
                        updatedSubtitles[originalIndex] = {
                            ...currentSubtitle,
                            // 更新音色相关的字段
                            origin_audio: newSubtitle.origin_audio || currentSubtitle.origin_audio,
                            voice_info: newSubtitle.voice_info || currentSubtitle.voice_info,
                            speakers: newSubtitle.speakers || currentSubtitle.speakers,
                            subtitle_id: newSubtitle.subtitle_id || currentSubtitle.subtitle_id,
                            // 保留其他字段不变
                            id: currentSubtitle.id,
                            task_id: currentSubtitle.task_id,
                            origin_text: currentSubtitle.origin_text,
                            post_text: currentSubtitle.post_text,
                            post_start_time: currentSubtitle.post_start_time,
                            post_end_time: currentSubtitle.post_end_time,
                            origin_start_time: currentSubtitle.origin_start_time,
                            origin_end_time: currentSubtitle.origin_end_time,
                            is_episode_voice: currentSubtitle.is_episode_voice,
                            trans_audio: currentSubtitle.trans_audio,
                            audio_speed: currentSubtitle.audio_speed,
                            volume_gain_db: currentSubtitle.volume_gain_db,
                            words: currentSubtitle.words || []
                        };
                        // 更换音色操作：已更新本地字幕数据
                    }
                }
                
                // 更新videoData - 确保创建新的对象引用
                const newVideoData: Task.GetTaskStatusResult = {
                    ...videoData,
                    subtitles: updatedSubtitles
                } as Task.GetTaskStatusResult;
                
                
                // 强制更新videoData
                setVideoDataSafe(newVideoData);
                
                // 如果是重新翻译或重新配音操作，同步更新 targetText 和 sourceText
                if (newSubtitle === null) {
                    const currentSubtitleId = (originalSubtitle as any).currentSubtitleId;
                    if (!currentSubtitleId) {
                        // 判断是重新翻译还是重新配音
                        const originalIndex = videoData.subtitles.findIndex(sub => sub.id === originalSubtitle.id);
                        if (originalIndex !== -1) {
                            const currentSubtitle = videoData.subtitles[originalIndex];
                            const isRetranslate = originalSubtitle.post_text && 
                                originalSubtitle.post_text !== currentSubtitle.post_text;
                            const operationType = (originalSubtitle as any).operationType;
                            const isRedub = operationType === 'redub';
                            
                            if (isRetranslate) {
                                // 更新 targetText
                                setTargetText(prev => prev.map(item => 
                                    item.id === originalSubtitle.id 
                                        ? { ...item, text: originalSubtitle.post_text || '' }
                                        : item
                                ));
                                
                                // 更新 sourceText（如果需要）
                                if (originalSubtitle.origin_text) {
                                    setSourcesText((prev: Array<{ id: number; text: string; timeRange: string }>) => 
                                        prev.map((item: { id: number; text: string; timeRange: string }) => 
                                            item.id === originalSubtitle.id 
                                                ? { ...item, text: originalSubtitle.origin_text }
                                                : item
                                        )
                                    );
                                }
                                
                                // 重新翻译后，已同步更新 targetText 和 sourceText
                            } else if (isRedub) {
                                // 重新配音操作：同步更新时间戳到 targetText 和 sourceText
                                const newPostTimeRange = originalSubtitle.post_start_time && originalSubtitle.post_end_time
                                    ? `${originalSubtitle.post_start_time} → ${originalSubtitle.post_end_time}`
                                    : null;
                                const newOriginTimeRange = originalSubtitle.origin_start_time && originalSubtitle.origin_end_time
                                    ? `${originalSubtitle.origin_start_time} → ${originalSubtitle.origin_end_time}`
                                    : null;
                                
                                // 更新 targetText 的时间戳
                                if (newPostTimeRange) {
                                    setTargetText(prev => prev.map(item => 
                                        item.id === originalSubtitle.id 
                                            ? { ...item, timeRange: newPostTimeRange }
                                            : item
                                    ));
                                }
                                
                                // 更新 sourceText 的时间戳
                                if (newOriginTimeRange) {
                                    setSourcesText((prev: Array<{ id: number; text: string; timeRange: string }>) => 
                                        prev.map((item: { id: number; text: string; timeRange: string }) => 
                                            item.id === originalSubtitle.id 
                                                ? { ...item, timeRange: newOriginTimeRange }
                                                : item
                                        )
                                    );
                                }
                                
                                // 同步更新时间轴（不刷新视频）
                                if (timelineEditorRef.current && (newPostTimeRange || newOriginTimeRange)) {
                                    const updatedSubtitle = updatedSubtitles[originalIndex];
                                    if (updatedSubtitle) {
                                        timelineEditorRef.current.updateSegment(updatedSubtitle.id, updatedSubtitle);
                                    }
                                }
                            }
                        }
                    }
                }
                
                // 拆分字幕之后：输出时间轴所有数据的字段和值
                // 拆分字幕之后 - 时间轴所有数据已更新
                // 拆分字幕之后的数据已更新
                
                // 强制清空哈希值，确保字幕同步useEffect能够重新计算
                setLastSubtitlesHash('');
                
                // 根据操作类型显示不同的成功消息
                if (newSubtitle === null) {
                    const currentSubtitleId = (originalSubtitle as any).currentSubtitleId;
                    if (currentSubtitleId) {
                        showSuccess('字幕合并成功');
                    } else {
                        // 根据操作类型显示提示信息（不再对比文本内容）
                        const operationType = (originalSubtitle as any).operationType;
                        if (operationType === 'retranslate') {
                            showSuccess('重新翻译成功');
                        } else if (operationType === 'redub') {
                            showSuccess('重新配音成功');
                        } else {
                            // 如果没有操作类型标识，回退到旧逻辑（兼容性）
                            const originalIndex = videoData.subtitles.findIndex(sub => sub.id === originalSubtitle.id);
                            if (originalIndex !== -1) {
                                const currentSubtitle = videoData.subtitles[originalIndex];
                                const isRetranslate = originalSubtitle.post_text && 
                                    originalSubtitle.post_text !== currentSubtitle.post_text;
                                if (isRetranslate) {
                                    showSuccess('重新翻译成功');
                                } else {
                                    showSuccess('重新配音成功');
                                }
                            } else {
                                showSuccess('操作成功');
                            }
                        }
                    }
                } else {
                    showSuccess('字幕拆分成功');
                }
            } else {
                showError('字幕拆分失败：未找到原始字幕');
            }
        } else {
            showError('字幕拆分失败：数据异常');
        }
    }, [videoData, setVideoDataSafe, showSuccess, showError, pendingTimelineUpdates, timelineEditorRef]);

    const handleGetNextSubtitleId = useCallback((currentId: number) => {
        if (!videoData?.subtitles) {
            return null;
        }
        
        // 找到当前字幕在数组中的索引
        const currentIndex = videoData.subtitles.findIndex(sub => sub.id === currentId);
        if (currentIndex === -1 || currentIndex === videoData.subtitles.length - 1) {
            return null; // 没有找到当前字幕或已经是最后一个
        }
        
        // 获取下一个字幕的ID
        const nextSubtitle = videoData.subtitles[currentIndex + 1];
        return nextSubtitle.id;
    }, [videoData]);

    const [sourcesText, setSourcesText] = useState<Array<{ id: number; text: string; timeRange: string }>>([]);

    // targetText and setTargetText are received as params from page level (shared with timebox)

    // 【修复补充】为 targetText 也创建"特洛伊木马"对象
    // 原因：时间轴拖拽、重新翻译等操作会更新 targetText 状态（生成新数组）。
    // 如果直接传给 SubtitleOverlayPlayer，会导致 props 变化从而触发组件重置/视频重载。
    // 但是，为了确保字幕能够实时更新，我们需要在 targetText 内容变化时创建新引用
    // 使用 JSON.stringify 来检测内容变化，但只在关键字段变化时才重建
    const targetTextForDemo = useMemo(() => {
        // 创建一个新的数组，包含 targetText 的所有数据
        // 这样当 targetText 内容变化时，会创建新引用，触发 SubtitleOverlayPlayer 重新解析字幕
        return targetText ? [...targetText] : [];
    }, [id, targetText]); // 当 ID 或 targetText 内容变化时重建

    // 获取下一个台词信息的函数（优先使用 targetText 中的最新时间戳）
    const handleGetNextSubtitle = useCallback((currentId: number) => {
        if (!videoData?.subtitles) {
            return null;
        }
        
        // 找到当前字幕在数组中的索引
        const currentIndex = videoData.subtitles.findIndex(sub => sub.id === currentId);
        if (currentIndex === -1 || currentIndex === videoData.subtitles.length - 1) {
            return null; // 没有找到当前字幕或已经是最后一个
        }
        
        // 获取下一个字幕
        const nextSubtitle = videoData.subtitles[currentIndex + 1];
        
        // 优先使用 targetText 中的最新时间戳（如果存在）
        const nextTargetData = targetText.find(item => item.id === nextSubtitle.id);
        let nextPostStartTime = nextSubtitle.post_start_time || nextSubtitle.origin_start_time;
        let nextPostEndTime = nextSubtitle.post_end_time || nextSubtitle.origin_end_time;
        
        if (nextTargetData && nextTargetData.timeRange) {
            // 从 targetText 中解析时间戳
            const timeRangeParts = nextTargetData.timeRange.split(' → ');
            if (timeRangeParts.length === 2) {
                nextPostStartTime = timeRangeParts[0].trim();
                nextPostEndTime = timeRangeParts[1].trim();
            }
        }
        
        // 下一句原音频：兼容 legacy(new snake_case) 和新版 camelCase 字段
        // 注意：这里避免强制把 legacy 类型转成 Task.Subtitles，防止 TS2352。
        const nextAny = nextSubtitle as any;
        const nextOriginAudioRaw =
            typeof nextAny?.origin_audio === 'string' ? nextAny.origin_audio : undefined;
        const nextOriginAudioRaw2 =
            typeof nextAny?.originAudio === 'string' ? nextAny.originAudio : undefined;
        const nextOriginAudio = nextOriginAudioRaw ?? nextOriginAudioRaw2;

        return {
            // 原台词时间戳
            originStartTime: nextSubtitle.origin_start_time,
            originEndTime: nextSubtitle.origin_end_time,
            // 目标台词时间戳（优先使用 targetText 中的最新时间戳）
            postStartTime: nextPostStartTime,
            postEndTime: nextPostEndTime,
            // 兼容旧版本
            startTime: nextSubtitle.origin_start_time,
            endTime: nextSubtitle.origin_end_time,
            // 下一句原音频（用于 RowItem 控制「合并台词」显隐）
            originAudio: nextOriginAudio ?? '',
        };
    }, [videoData, targetText]);

    // 获取完整时间轴数据的回调函数
    const handleGetFullTimelineData = useCallback(() => {
        // 获取完整时间轴数据
        // 完整时间轴数据已生成
        
        return {
            videoDataSubtitles: videoData?.subtitles,
            sourcesText,
            targetText
        };
    }, [videoData, sourcesText, targetText]);

    const getLatestSubtitleData = (subtitleId: number, field: 'origin_text' | 'post_text', newText: string) => {
        // 从videoData.subtitles中获取基础数据
        const subtitle = videoData?.subtitles?.find(sub => sub.id === subtitleId);
        if (!subtitle) return null;
        
        // 从状态中获取最新的编辑数据
        const currentTargetData = targetText.find(item => item.id === subtitleId);
        const currentSourceData = sourcesText.find(item => item.id === subtitleId);
        
        // 构建最新的数据
        const latestData = {
            ...subtitle,
            origin_text: field === 'origin_text' ? newText : (currentSourceData?.text || subtitle.origin_text || ''),
            post_text: field === 'post_text' ? newText : (currentTargetData?.text || subtitle.post_text || '')
        };
        
        // 获取最新字幕数据
        
        return latestData;
    };

    // 构建字幕更新参数的辅助函数
    const buildSubtitleUpdateParams = (subtitleId: number, field: 'origin_text' | 'post_text', newText: string): Subtitles.UpdateSubtitleParams | null => {
        if (!videoData?.subtitles) {
            // 视频数据或字幕数据不存在
            return null;
        }
        
        const subtitle = videoData.subtitles.find(sub => sub.id === subtitleId);
        if (!subtitle) {
            // 未找到指定ID的字幕数据
            return null;
        }
        
        // 获取最新的字幕数据
        const latestData = getLatestSubtitleData(subtitleId, field, newText);
        if (!latestData) {
            // 获取最新字幕数据失败
            return null;
        }
        
        return {
            task_id: videoData.id,
            // 使用最新的数据
            origin_text: latestData.origin_text,
            post_text: latestData.post_text,
            post_start_time: latestData.post_start_time || latestData.origin_start_time || '00:00:00,000',
            post_end_time: latestData.post_end_time || latestData.origin_end_time || '00:00:01,000',
            origin_start_time: latestData.origin_start_time || '00:00:00,000',
            origin_end_time: latestData.origin_end_time || '00:00:01,000',
            is_episode_voice: latestData.is_episode_voice || false,
            trans_audio: latestData.trans_audio || '',
            subtitle_id: latestData.subtitle_id || latestData.id,
            score: 98.9509, // 默认权重
            audio_speed: latestData.audio_speed || 1,
            id: latestData.id,
            speaker_id: latestData.speakers?.id || 0
        };
    };

    // 源语言数据更新处理
    const handleSourceDataChange = async (newData: { id: number; text: string; timeRange: string }) => {
        // 检查台词是否有变化
        const currentSourceData = sourcesText.find(item => item.id === newData.id);
        if (currentSourceData && currentSourceData.text === newData.text) {
            // 左侧台词没有变化，跳过API调用
            return;
        }
        
        // 先更新本地状态（保留 voiceSourceType、publicVoiceToneId、userVoiceToneId 等字段）
        setSourcesText(prev => prev.map(item => item.id === newData.id ? { ...item, ...newData } : item));
        
        // 调用API更新（新接口只支持更新originText）
        setUpdatingSubtitles(prev => ({ ...prev, [newData.id]: true }));
        setSubtitleUpdateErrors(prev => ({ ...prev, [newData.id]: '' }));
        
        try {
            const response = await updateSubtitleApi({
                id: newData.id,
                originText: newData.text
            });
            // 更新台词API响应已接收
            
            if (response.code === 0 && response.data) {
                const originalSubtitle = videoData?.subtitles?.find(sub => sub.id === newData.id);
                if (originalSubtitle?.trans_audio) {
                    evictTargetTransAudioCache(newData.id, originalSubtitle.trans_audio);
                }

                // 同步更新videoData中的origin_text和post_text
                setVideoData(prev => {
                    if (!prev?.subtitles) return prev;
                    const updatedSubtitles = prev.subtitles.map(subtitle => {
                        if (subtitle.id === newData.id) {
                            return {
                                ...subtitle,
                                origin_text: response.data.originText || newData.text,
                                post_text: '', // 修改源台词后，清空对应的目标台词
                                trans_audio: '', // 同步清空目标音频，避免仍用旧缓存播放
                            };
                        }
                        return subtitle;
                    });
                    return {
                        ...prev,
                        subtitles: updatedSubtitles
                    };
                });
                
                // 更新时间轴显示 - 使用API返回的完整数据
                if (timelineEditorRef.current && response.data) {
                    // 将API返回的Task.Subtitles格式转换为Task.SubtitlesLegacy格式
                    const subtitleForTimeline: Task.SubtitlesLegacy = {
                        id: response.data.id,
                        task_id: response.data.taskId,
                        origin_text: response.data.originText,
                        post_text: response.data.postText || '',
                        post_start_time: response.data.postStartTime || '',
                        post_end_time: response.data.postEndTime || '',
                        origin_start_time: response.data.originStartTime,
                        origin_end_time: response.data.originEndTime,
                        is_episode_voice: response.data.isEpisodeVoice || false,
                        origin_audio: response.data.originAudio,
                        trans_audio: '', // 修改原文后目标音频失效，不沿用接口可能残留的旧值
                        subtitle_id: response.data.subtitleId || response.data.id,
                        audio_speed: response.data.audioSpeed || 1.0,
                        volume_gain_db: response.data.volumeGainDb || 1.0,
                        speakers: response.data.speaker ? {
                            id: response.data.speaker.id,
                            series_id: response.data.speaker.seriesId,
                            task_id: response.data.taskId,
                            speaker_name: response.data.speaker.speakerName,
                            description: response.data.speaker.description || '',
                            is_series_speaker: response.data.speaker.isSeriesSpeaker
                        } : (originalSubtitle?.speakers || {} as any),
                        voice_info: originalSubtitle?.voice_info || {} as any
                    };
                    
                    timelineEditorRef.current.updateSegment(newData.id, subtitleForTimeline);
                }
                
                // 更新sourcesText状态
                setSourcesText(prev => prev.map(item => 
                    item.id === newData.id ? { ...item, text: newData.text } : item
                ));
                
                // 修改源台词后，清空对应的目标台词
                setTargetText(prev => prev.map(item => 
                    item.id === newData.id ? { ...item, text: '' } : item
                ));
                
                // 更新sourceText数据和时间轴显示
                
                showSuccess('台词更新成功');
            } else {
                throw new Error(response.msg || '更新失败');
            }
        } catch (error: any) {
            console.error('更新台词失败:', error);
            setSubtitleUpdateErrors(prev => ({ 
                ...prev, 
                [newData.id]: error?.message || '更新失败' 
            }));
            showError('台词更新失败');
        } finally {
            setUpdatingSubtitles(prev => ({ ...prev, [newData.id]: false }));
        }
    };

    // 更新videoData中的语速数据
    const handleUpdateVideoDataSpeed = (subtitleId: number, audioSpeed: number) => {
        setVideoData(prev => {
            if (!prev?.subtitles) return prev;
            
            const updatedSubtitles = prev.subtitles.map(subtitle => {
                if (subtitle.id === subtitleId) {
                    return {
                        ...subtitle,
                        audio_speed: audioSpeed
                    };
                }
                return subtitle;
            });
            
            // 更新videoData中的语速数据
            
            return {
                ...prev,
                subtitles: updatedSubtitles
            };
        });
    };

    const handleTargetDataChange = async (newData: { id: number; text: string; timeRange: string }) => {
        const sanitizedText = normalizeTargetPostTextFromEditor(newData.text);
        const payload = { ...newData, text: sanitizedText };

        // 检查台词是否有变化
        const currentTargetData = targetText.find(item => item.id === newData.id);
        const hasTextChange =
            currentTargetData &&
            !isPostTextEqualIgnoringMarkers(sanitizedText, currentTargetData.text);
        const hasTimeChange = currentTargetData && currentTargetData.timeRange !== newData.timeRange;
        
        if (!hasTextChange && !hasTimeChange) {
            // 右侧台词和时间戳都没有变化，跳过API调用
            return;
        }
        
        // 先更新本地状态
        setTargetText(prev => prev.map(item => (item.id === newData.id ? payload : item)));
        
        // 如果有时间戳变化，同步更新videoData.subtitles
        if (hasTimeChange && videoData?.subtitles) {
            const [newStartTime, newEndTime] = newData.timeRange.split(' → ');
            setVideoData(prev => {
                if (!prev?.subtitles) return prev;
                
                const updatedSubtitles = prev.subtitles.map(subtitle => {
                    if (subtitle.id === newData.id) {
                        return {
                            ...subtitle,
                            post_start_time: newStartTime,
                            post_end_time: newEndTime
                        };
                    }
                    return subtitle;
                });
                
                // 同步更新时间轴字幕数据
                
                return {
                    ...prev,
                    subtitles: updatedSubtitles
                };
            });
        }
        
        // 如果有文本变化，调用新接口仅更新 post_text
        if (hasTextChange) {
            try {
                setUpdatingSubtitles(prev => ({ ...prev, [newData.id]: true }));

                // 调用后端接口更新目标语言文本
                const response = await updatePostTextApi({
                    id: newData.id,
                    postText: sanitizedText,
                });

                if (response.code !== 0) {
                    throw new Error(response.msg || '更新失败');
                }

                // updatePostTextApi 更新成功

                // 同步更新本地 targetText 状态
                setTargetText(prev => prev.map(item =>
                    item.id === newData.id ? { ...item, text: sanitizedText } : item
                ));

                // 同步更新 videoData.subtitles 中的 post_text
                setVideoData(prev => {
                    if (!prev?.subtitles) return prev;
                    const updatedSubtitles = prev.subtitles.map(subtitle =>
                        subtitle.id === newData.id
                            ? { ...subtitle, post_text: sanitizedText }
                            : subtitle
                    );
                    return {
                        ...prev,
                        subtitles: updatedSubtitles
                    };
                });

                // 更新时间轴显示（使用最新数据刷新对应片段）
                if (videoData?.subtitles && timelineEditorRef.current) {
                    const latestData = getLatestSubtitleData(newData.id, 'post_text', sanitizedText);
                    if (latestData) {
                        timelineEditorRef.current.updateSegment(newData.id, latestData);
                    }
                }

                // 已同步更新 targetText、videoData.subtitles 和时间轴显示

                showSuccess('台词更新成功');
            } catch (error: any) {
                console.error('❌ 更新目标语言台词失败:', error);
                setSubtitleUpdateErrors(prev => ({
                    ...prev,
                    [newData.id]: error?.message || '更新失败'
                }));
                showError(error?.message || '更新目标语言台词失败');
            } finally {
                setUpdatingSubtitles(prev => ({ ...prev, [newData.id]: false }));
            }
        }
    };

    const handleRoleSelect = async (roleName: string, rowId: number, speakerId?: string, isSeriesVoiceLibrary?: boolean, batchMode?: boolean) => {
        setRowSelectedRoles(prev => ({
            ...prev,
            [rowId]: roleName
        }));
        
        // 如果角色名称为空，清除剧集音色库标记（移出音色库的情况）
        if (!roleName) {
            setSeriesVoiceLibraryMarkers(prev => {
                const newMarkers = { ...prev };
                delete newMarkers[rowId];
                // 已清除剧集音色库标记
                return newMarkers;
            });
        }
        
        // 如果是剧集音色库操作，只更新本地状态，不调用API和更新videoData
        if (isSeriesVoiceLibrary) {
            // 剧集音色库操作 - 仅更新本地状态
            
            // 更新剧集音色库标记状态
            setSeriesVoiceLibraryMarkers(prev => ({
                ...prev,
                [rowId]: true
            }));
            
            // 已更新剧集音色库标记状态
            return;
        }
        
        // 输出说话人信息到控制台
        if (speakerId) {
            // 台词行选择说话人
            
            try {
                const numericSpeakerId = parseInt(speakerId, 10);
                
                // 检查当前字幕的说话人ID是否和选择的相同，如果相同则不调用接口
                const currentSubtitle = videoData?.subtitles?.find(sub => sub.id === rowId);
                const currentSpeakerId = currentSubtitle?.speakers?.id;
                
                if (currentSpeakerId === numericSpeakerId) {
                    // ID 未变则不调接口；若本地 speaker_name 与所选名称不一致，对齐 videoData（避免展示名与数据漂移）
                    const nameOutOfSync =
                        currentSubtitle?.speakers &&
                        (currentSubtitle.speakers.speaker_name?.trim() ?? '') !== roleName.trim();
                    if (nameOutOfSync && videoData?.subtitles) {
                        setVideoData(prev => {
                            if (!prev?.subtitles) return prev;
                            const updatedSubtitles = prev.subtitles.map(sub =>
                                sub.speakers?.id === numericSpeakerId
                                    ? {
                                          ...sub,
                                          speakers: {
                                              ...sub.speakers!,
                                              speaker_name: roleName,
                                          },
                                      }
                                    : sub
                            );
                            return { ...prev, subtitles: updatedSubtitles };
                        });
                    }
                    if (videoData?.id) {
                        await fetchSpeakersList(videoData.id);
                    }
                    return;
                }

                // 纯净版说话人与字幕校对 + 勾选了批量模式：走批量修改说话人接口
                if (isFromPure && batchMode) {
                    const taskId = videoData?.id;
                    if (!taskId || currentSpeakerId == null) {
                        showError('缺少任务ID或原说话人信息');
                        return;
                    }
                    const batchResponse = await batchChangeSubtitleSpeakerApi(taskId, currentSpeakerId, numericSpeakerId);
                    if (batchResponse.code === 0) {
                        // 本地更新所有原说话人的字幕为新说话人
                        setVideoData(prev => {
                            if (!prev?.subtitles) return prev;
                            const updatedSubtitles = prev.subtitles.map(sub =>
                                sub.speakers?.id === currentSpeakerId
                                    ? { ...sub, speakers: { ...sub.speakers!, id: numericSpeakerId, speaker_name: roleName } }
                                    : sub
                            );
                            return { ...prev, subtitles: updatedSubtitles };
                        });
                        // 清除所有匹配行的 rowSelectedRoles，强制 selectedRole 从 videoData 读取最新数据
                        setRowSelectedRoles(prev => {
                            const next = { ...prev };
                            for (const key of Object.keys(next)) {
                                const k = Number(key);
                                if (!Number.isFinite(k)) continue;
                                const sub = videoData?.subtitles?.find(s => s.id === k);
                                if (sub?.speakers?.id === currentSpeakerId) {
                                    delete next[k];
                                }
                            }
                            return next;
                        });
                        await fetchSpeakersList(taskId);
                        showSuccess(`说话人已批量更新为"${roleName}"`);
                    } else {
                        showError(batchResponse.msg || '批量修改说话人失败');
                    }
                    return;
                }

                // 正在调用 changeSubtitleSpeakerApi 修改说话人（非纯净版，逐条修改）

                const response = await changeSubtitleSpeakerApi(rowId, numericSpeakerId);

                if (response.code === 0) {
                    // 修改字幕说话人成功

                    // 更新本地数据
                    if (videoData?.subtitles) {
                        const updatedSubtitles = videoData.subtitles.map(sub => 
                            sub.id === rowId 
                                ? { 
                                    ...sub, 
                                    speakers: { 
                                        ...sub.speakers, 
                                        id: numericSpeakerId,
                                        speaker_name: roleName 
                                    } 
                                }
                                : sub
                        );
                        
                        setVideoData(prev => prev ? { ...prev, subtitles: updatedSubtitles } : null);
                        await fetchSpeakersList(videoData.id);
                    }
                    
                    showSuccess(`说话人已更新为"${roleName}"`);
                } else {
                    console.error('❌ 修改字幕说话人失败:', response);
                    showError(response.msg || '说话人更新失败');
                }
            } catch (error: any) {
                console.error('❌ 修改字幕说话人接口调用出错:', error);
                showError(error?.message || '说话人更新失败');
            }
        } else {
            // 台词行选择角色（非API创建的说话人，无speaker_id）
        }
    };

    // 新建说话人处理（创建后自动将当前台词切换为该说话人）
    const handleNewSpeaker = async (rowId?: number, speakerName?: string) => {
        if (!videoData) {
            showError('视频数据不存在，无法创建说话人');
            return;
        }

        try {
            const customSpeakerName = speakerName?.trim();
            if (customSpeakerName) {
                const response = await addVideoSpeakerApi(videoData.id, customSpeakerName);

                if (response && response.code === 0) {
                    await fetchSpeakersList(videoData.id);
                    setEditingRoleKey(null);
                    setEditingRoleName('');
                    showSuccess(`创建说话人"${customSpeakerName}"成功`);
                } else {
                    showError(response.msg || '创建说话人失败');
                }
                return;
            }

            // 生成默认的说话人名称（按顺序）
            // 从现有角色中找出最大的角色序号
            let maxRoleNumber = 0;
            roles.forEach(role => {
                const match = role.label.match(/^角色(\d+)$/);
                if (match) {
                    const number = parseInt(match[1]);
                    if (number > maxRoleNumber) {
                        maxRoleNumber = number;
                    }
                }
            });
            
            // 生成下一个角色序号
            const nextRoleNumber = maxRoleNumber + 1;
            const defaultSpeakerName = `角色${nextRoleNumber}`;
            
            // 生成新说话人名称
            
            // 调用API创建说话人（使用新接口）
            const response = await addVideoSpeakerApi(videoData.id, defaultSpeakerName);
            
            if (response && response.code === 0) {
                // 说话人创建成功
                
                // 创建成功后，重新获取说话人列表以确保数据同步
                const updatedRoles = await fetchSpeakersList(videoData.id);
                
                // 从更新后的列表中查找新创建的说话人（通过名称匹配）
                const newRole = updatedRoles.find(role => role.label === defaultSpeakerName);
                
                if (newRole) {
                    // 设置编辑状态
                    setEditingRoleKey(newRole.key);
                    setEditingRoleName(newRole.label);
                    showSuccess(`创建说话人"${defaultSpeakerName}"成功，可以修改名称`);

                    // 如果传入了当前台词ID，则自动把该台词的说话人切换为新建的说话人
                    if (rowId) {
                        // 从 role.key 解析 speakerId：speaker_<id> / series_speaker_<id>
                        let speakerId: string | undefined;
                        if (typeof newRole.key === 'string') {
                            const m = newRole.key.match(/^(?:speaker_|series_speaker_)(\d+)$/);
                            if (m) speakerId = m[1];
                        }

                        // 自动应用新建说话人到当前台词

                        // 更新当前行的角色选择（UI 显示）
                        setRowSelectedRoles(prev => ({
                            ...prev,
                            [rowId]: newRole.label
                        }));

                        // 如果解析出了 speakerId，则调用现有的角色选择逻辑，更新后端与本地状态
                        if (speakerId) {
                            await handleRoleSelect(newRole.label, rowId, speakerId);
                        } else {
                            // 未能从新建说话人中解析 speakerId，跳过字幕说话人更新
                        }
                    }
                } else {
                    // 如果找不到，说明可能创建失败或名称已存在，重新获取列表
                    // 新创建的角色未在列表中，重新获取列表
                    await fetchSpeakersList(videoData.id);
                    showSuccess(`创建说话人"${defaultSpeakerName}"成功`);
                }
            } else {
                showError(response.msg || '创建说话人失败');
            }
        } catch (error: any) {
            console.error('创建说话人失败:', error);
            showError(error?.message || '创建说话人失败');
        }
    };

    /** 说话人下拉中点击「删除」：仅支持 key 为 speaker_<id> 或 series_speaker_<id> 的说话人；删除后使用该说话人的台词显示为「未知说话人」 */
    const handleDeleteRole = useCallback(async (roleKey: string) => {
        const match = roleKey.match(/^(?:speaker_|series_speaker_)(\d+)$/);
        if (!match) {
            showError('仅支持删除已同步的说话人');
            return;
        }
        const role = roles.find(r => r.key === roleKey);
        const deletedLabel = role?.label ?? '';
        const speakerId = parseInt(match[1], 10);
        try {
            await deleteSpeakerApi(speakerId);
            setRoles(prev => prev.filter(r => r.key !== roleKey));
            // 使用该说话人的台词行改为显示「未知说话人」
            setRowSelectedRoles(prev => {
                const next = { ...prev };
                Object.keys(next).forEach(idStr => {
                    const id = Number(idStr);
                    if (prev[id] === deletedLabel) next[id] = '未知说话人';
                });
                return next;
            });
            showSuccess('已删除该说话人');
        } catch (e: any) {
            console.error('删除说话人失败:', e);
            showError(e?.message || '删除失败，请重试');
        }
    }, [roles]);

    const handleStartEditRole = (roleKey: string, currentName: string) => {
        setEditingRoleKey(roleKey);
        setEditingRoleName(currentName);
    };

    // 保存角色编辑
    const handleSaveRoleEdit = useCallback(async (roleKey: string): Promise<void> => {
        // 防重复调用
        if (isSavingRole) {
            // 正在保存角色编辑，跳过重复调用
            return;
        }

        if (editingRoleName.trim()) {
            const currentRole = roles.find(r => r.key === roleKey);
            const newName = editingRoleName.trim();

            // 如果名称没有变化，直接返回，不调用接口
            if (currentRole && currentRole.label === newName) {
                // 说话人名称未变化，跳过接口调用
                setEditingRoleKey(null);
                setEditingRoleName('');
                return;
            }

            // 只有当名称真正发生变化时才更新和提示
            if (currentRole && currentRole.label !== newName) {
                try {
                    setIsSavingRole(true);
                    // 如果是通过API创建的说话人（key格式为speaker_xxx），则调用API更新
                    if (roleKey.startsWith('speaker_')) {
                        const speakerId = parseInt(roleKey.replace('speaker_', ''));

                        const response = await renameVideoSpeakerApi(speakerId, newName);
                        
                        if (response && response.code === 0) {
                            // 说话人重命名API调用成功，直接更新本地状态
                            
                            // 直接更新本地状态，不进行验证
                            setRoles(prev => prev.map(role => 
                                role.key === roleKey ? { ...role, label: newName } : role
                            ));

                            setVideoData(prev => {
                                if (!prev?.subtitles) return prev;
                                const updatedSubtitles = prev.subtitles.map(subtitle => {
                                    if (subtitle.speakers?.id !== speakerId) {
                                        return subtitle;
                                    }
                                    return {
                                        ...subtitle,
                                        speakers: {
                                            ...subtitle.speakers,
                                            speaker_name: newName
                                        }
                                    };
                                });
                                return {
                                    ...prev,
                                    subtitles: updatedSubtitles
                                };
                            });
                            
                            // 如果编辑的是当前选中的角色，也要更新rowSelectedRoles中对应的角色
                            Object.keys(rowSelectedRoles).forEach(rowId => {
                                if (rowSelectedRoles[parseInt(rowId)] === currentRole.label) {
                                    setRowSelectedRoles(prev => ({
                                        ...prev,
                                        [parseInt(rowId)]: newName
                                    }));
                                }
                            });

                            showSuccess(`说话人名称修改成功: "${currentRole.label}" → "${newName}"`);
                        } else {
                            console.error('❌ 说话人重命名API调用失败:', response);
                            showError(response.msg || '说话人名称修改失败');
                            throw new Error(response.msg || '说话人名称修改失败');
                        }
                    } else {
                        // 对于非API创建的说话人，只更新本地状态
                        const updatedRoles = roles.map(role =>
                            role.key === roleKey
                                ? { ...role, label: newName }
                                : role
                        );
                        setRoles(updatedRoles);

                        // 如果编辑的是当前选中的角色，也要更新rowSelectedRoles中对应的角色
                        Object.keys(rowSelectedRoles).forEach(rowId => {
                            if (rowSelectedRoles[parseInt(rowId)] === currentRole.label) {
                                setRowSelectedRoles(prev => ({
                                    ...prev,
                                    [parseInt(rowId)]: newName
                                }));
                            }
                        });

                        showSuccess('角色名称修改成功');
                    }
                } catch (error: any) {
                    showError(error?.message || '说话人名称修改失败');
                    throw error; // 重新抛出错误，让调用者知道保存失败
                } finally {
                    setIsSavingRole(false);
                }
            }

            // 仅当当前仍在该角色编辑状态时才清空，避免“先 blur 保存再点击另一项编辑”时清空新编辑状态
            setEditingRoleKey(prev => {
                if (prev === roleKey) {
                    setEditingRoleName('');
                    return null;
                }
                return prev;
            });
        }
    }, [isSavingRole, editingRoleName, roles, rowSelectedRoles, showSuccess, showError]);

    // 取消角色编辑
    const handleCancelRoleEdit = () => {
        setEditingRoleKey(null);
        setEditingRoleName('');
    };

    const [highlightedSubtitleId, setHighlightedSubtitleId] = useState<number | null>(null);
    /** 与 highlightedSubtitleId 同步；用于 startTransition 期间去重，避免同一句多次 scroll / transition */
    const highlightedSubtitleCommittedRef = useRef<number | null>(null);

    useEffect(() => {
        highlightedSubtitleCommittedRef.current = highlightedSubtitleId;
    }, [highlightedSubtitleId]);

    const textListRef = useRef<HTMLDivElement>(null);

    const scrollToHighlightedSubtitle = useCallback((subtitleId: number) => {
        const id = subtitleId;
        // 单次 rAF + 单次布局读，避免同步 getBoundingClientRect 与 rAF 内再读造成双次 reflow
        requestAnimationFrame(() => {
            const container = textListRef.current;
            if (!container) return;

            const targetElement = container.querySelector(`[data-subtitle-id="${id}"]`) as HTMLElement | null;
            if (!targetElement) return;

            const isPlaying = usePlaybackStore.getState().isPlaying;
            const edgeMargin = 4;
            const containerRect = container.getBoundingClientRect();
            const targetRect = targetElement.getBoundingClientRect();

            if (isPlaying) {
                const completelyOutside =
                    targetRect.bottom <= containerRect.top + edgeMargin ||
                    targetRect.top >= containerRect.bottom - edgeMargin;
                if (!completelyOutside) {
                    return;
                }
            }

            const offsetTop = targetRect.top - containerRect.top;
            const nextTop =
                container.scrollTop +
                offsetTop -
                container.clientHeight / 2 +
                targetElement.clientHeight / 2;

            container.scrollTo({
                top: nextTop,
                behavior: isPlaying ? 'auto' : 'smooth',
            });
        });
    }, []);

    // 字幕时间戳仅在数据变化时解析为数字索引；播放路径不再逐帧 parse 字符串
    const subtitleHighlightIndex = useMemo(() => {
        if (!videoData?.subtitles?.length) return null;
        return buildSubtitleHighlightIndex(videoData.subtitles);
    }, [videoData?.subtitles, videoData?.id]);

    // 根据时间高亮对应的目标台词（同时检查原台词和目标台词的时间范围）
    // 重合区间内取“起始时间更晚”的那条，避免下键切到下一句时高亮被上一句抢回
    const updateTargetSubtitleHighlight = useCallback(
        (timeInSeconds: number) => {
            try {
                if (!subtitleHighlightIndex) {
                    return;
                }

                const activeTrack = timelineEditorRef.current?.activeTrack || 'translated';
                const useOriginTimes = activeTrack === 'original';

                const matchedSubtitleId = findSubtitleHighlightMatch(
                    subtitleHighlightIndex,
                    timeInSeconds,
                    useOriginTimes
                );

                if (
                    matchedSubtitleId !== null &&
                    matchedSubtitleId !== highlightedSubtitleCommittedRef.current
                ) {
                    highlightedSubtitleCommittedRef.current = matchedSubtitleId;
                    startTransition(() => {
                        setHighlightedSubtitleId(matchedSubtitleId);
                    });
                    scrollToHighlightedSubtitle(matchedSubtitleId);
                }

                // 注意：字幕渲染现在在handleTimeUpdate中立即执行，确保字幕跟随时间戳准确显示/消失
            } catch (error) {
                console.error('更新目标台词高亮失败:', error);
            }
        },
        [subtitleHighlightIndex, scrollToHighlightedSubtitle]
    );

    // 字幕数据缓存，避免重复计算
    const [lastSubtitlesHash, setLastSubtitlesHash] = useState<string>('');

    // 根据API数据同步字幕 - 优化版本，避免不必要的重新计算
    useEffect(() => {
        try {
            // 从新的数据结构中获取字幕数据：直接访问subtitles
            const subtitles = videoData?.subtitles;
            
            if (subtitles && Array.isArray(subtitles)) {
                // 计算字幕数据的哈希值，避免重复计算
                // 注意：必须包含 subtitle_id，因为更换音色后会改变 subtitle_id
                const subtitlesHash = JSON.stringify(subtitles.map(sub => ({
                    id: sub.id,
                    subtitle_id: sub.subtitle_id, // 添加 subtitle_id 到哈希计算中
                    origin_text: sub.origin_text,
                    post_text: sub.post_text,
                    origin_start_time: sub.origin_start_time,
                    origin_end_time: sub.origin_end_time,
                    post_start_time: sub.post_start_time,
                    post_end_time: sub.post_end_time,
                    speakers: sub.speakers
                        ? { id: sub.speakers.id, speaker_name: sub.speakers.speaker_name }
                        : null,
                })));

                // 如果字幕数据没有变化，跳过重新计算
                if (subtitlesHash === lastSubtitlesHash) {
                    // 字幕数据哈希值未变化，跳过更新
                    return;
                }
                
                // 字幕数据哈希值已变化，开始更新 sourcesText 和 targetText
                

                const startTime = performance.now();

                // 生成目标语言字幕数据（使用post_text作为翻译后的文本）
                // 优先使用targetText状态中的数据，避免丢失用户编辑的内容
                const generatedTargetText = subtitles.map((subtitle: Task.SubtitlesLegacy, index: number) => {
                    // 查找当前字幕在targetText状态中是否有用户编辑的数据
                    const existingTargetData = targetText.find(item => item.id === subtitle.id);
                    
                    return {
                        id: subtitle.id, // 确保使用真实的字幕ID，不使用fallback
                        subtitle_id: subtitle.subtitle_id, // 添加subtitle_id字段用于音色相关操作
                        // 优先使用targetText状态中的数据，如果不存在则使用subtitle.post_text
                        text: existingTargetData?.text || subtitle.post_text || '',
                        timeRange: `${subtitle.post_start_time || subtitle.origin_start_time} → ${subtitle.post_end_time || subtitle.origin_end_time}`
                    };
                });

                setTargetText(generatedTargetText);
                
                // 调试：输出更新后的 subtitle_id 信息
                console.log('✅ targetText 已更新，subtitle_id 映射:', 
                    generatedTargetText.map(item => ({ id: item.id, subtitle_id: item.subtitle_id }))
                );

                // 同时更新源语言字幕数据（使用origin_text作为原始文本）
                // 优先使用sourcesText状态中的数据，避免丢失用户编辑的内容
                const generatedSourceText = subtitles.map((subtitle: Task.SubtitlesLegacy, index: number) => {
                    // 查找当前字幕在sourcesText状态中是否有用户编辑的数据
                    const existingSourceData = sourcesText.find(item => item.id === subtitle.id);
                    
                    return {
                        id: subtitle.id, // 确保使用真实的字幕ID，不使用fallback
                        subtitle_id: subtitle.subtitle_id, // 添加subtitle_id字段用于音色相关操作
                        task_id: subtitle.task_id, // 添加task_id字段
                        speaker_id: subtitle.speakers?.id || 0, // 添加speaker_id字段
                        // 优先使用sourcesText状态中的数据，如果不存在则使用subtitle.origin_text
                        text: existingSourceData?.text || subtitle.origin_text || '',
                        timeRange: `${subtitle.origin_start_time} → ${subtitle.origin_end_time}`,
                        voiceSourceType: subtitle.voiceSourceType,
                        publicVoiceToneId: subtitle.publicVoiceToneId,
                        userVoiceToneId: subtitle.userVoiceToneId
                    };
                });

                setSourcesText(generatedSourceText);
                
                // 调试：输出更新后的 subtitle_id 信息
                // sourcesText 已更新
                
                // 使用generatedTargetText和generatedSourceText的数据重新同步videoData.subtitles
                setVideoData(prev => {
                    if (!prev?.subtitles) return prev;
                    
                    const updatedSubtitles = prev.subtitles.map((subtitle: Task.SubtitlesLegacy) => {
                        // 查找generatedTargetText和generatedSourceText中对应的数据
                        const targetData = generatedTargetText.find(item => item.id === subtitle.id);
                        const sourceData = generatedSourceText.find(item => item.id === subtitle.id);
                        
                        return {
                            ...subtitle,
                            // 使用generatedTargetText和generatedSourceText中的数据更新videoData.subtitles
                            post_text: targetData?.text || subtitle.post_text || '',
                            origin_text: sourceData?.text || subtitle.origin_text || ''
                        };
                    });
                    
                    // 使用generatedTargetText和generatedSourceText重新同步videoData.subtitles
                    return {
                        ...prev,
                        subtitles: updatedSubtitles
                    };
                });
                
                // 字幕同步useEffect重新计算后 - 时间轴所有数据已更新
                
                // 更新时间轴显示，使用重新同步后的videoData.subtitles数据
                if (timelineEditorRef.current) {
                    // 重新生成时间轴片段，使用重新同步后的videoData.subtitles数据
                    timelineEditorRef.current.generateSegmentsFromSubtitles(subtitles);
                }
                
                // 更新缓存哈希值
                setLastSubtitlesHash(subtitlesHash);
                
                const endTime = performance.now();
            }

        } catch (error) {
            console.error('字幕数据同步出错:', error);
            // 如果出错，保持默认数据不变
        }
    }, [videoData?.subtitles, seriesId, lastSubtitlesHash]); // 添加lastSubtitlesHash依赖

    const handleRefreshSubtitles = useCallback(async () => {
        if (!id) {
            console.warn('视频ID不存在，无法刷新字幕列表');
            return;
        }

        try {
            const subtitlesResponse = await getSubtitlesListApi(parseInt(id));
            if (subtitlesResponse.code === 0 && subtitlesResponse.data) {
                // 将新接口格式（驼峰命名）转换为旧格式（下划线命名，保持兼容性）
                const subtitlesLegacy: Task.SubtitlesLegacy[] = subtitlesResponse.data.map((subtitle: Task.Subtitles) => ({
                    task_id: subtitle.taskId,
                    origin_text: subtitle.originText,
                    post_text: subtitle.postText || '',
                    post_start_time: subtitle.postStartTime || '',
                    post_end_time: subtitle.postEndTime || '',
                    origin_start_time: subtitle.originStartTime,
                    origin_end_time: subtitle.originEndTime,
                    origin_post_time: subtitle.originPostTime,
                    is_episode_voice: subtitle.isEpisodeVoice || false,
                    origin_audio: subtitle.originAudio,
                    trans_audio: subtitle.transAudio || '',
                    subtitle_id: subtitle.subtitleId || subtitle.id,
                    score: subtitle.score,
                    audio_speed: subtitle.audioSpeed || 1.0,
                    volume_gain_db: subtitle.volumeGainDb || 1.0,
                    id: subtitle.id,
                    speakers: {
                        id: subtitle.speaker.id,
                        series_id: subtitle.speaker.seriesId,
                        task_id: subtitle.taskId,
                        speaker_name: subtitle.speaker.speakerName,
                        description: subtitle.speaker.description || '',
                        is_series_speaker: subtitle.speaker.isSeriesSpeaker
                    },
                    voice_info: {
                        origin_text: subtitle.originText,
                        origin_start_time: subtitle.originStartTime,
                        origin_end_time: subtitle.originEndTime,
                        origin_audio: subtitle.originAudio,
                        score: subtitle.score
                    },
                    back_translate_text: subtitle.backTranslateText || '',
                    words: subtitle.words?.map(word => ({
                        id: word.id,
                        subtitle_id: word.subtitleId,
                        position: word.position,
                        origin_char: word.originChar,
                        start_ms: word.startMs,
                        end_ms: word.endMs
                    })) || [],
                    notes: subtitle.remark || undefined,
                    voiceSourceType: subtitle.voiceSourceType,
                    publicVoiceToneId: subtitle.publicVoiceToneId,
                    userVoiceToneId: subtitle.userVoiceToneId
                }));

                // 更新videoData中的subtitles
                if (videoData) {
                    setVideoDataSafe({
                        ...videoData,
                        subtitles: subtitlesLegacy
                    });
                    console.log('✅ 字幕列表已更新，数量:', subtitlesLegacy.length);
                }
            }
        } catch (error) {
            console.error('❌ 重新获取字幕列表失败:', error);
            throw error;
        }
    }, [id, videoData, setVideoDataSafe]);

    return {
        // State
        updatingSubtitles,
        setUpdatingSubtitles,
        subtitleUpdateErrors,
        setSubtitleUpdateErrors,
        deduplicateRoles,
        openHistorySubtitleId,
        setOpenHistorySubtitleId,
        volumeMap,
        setVolumeMap,
        speedMap,
        setSpeedMap,
        rowSelectedRoles,
        setRowSelectedRoles,
        seriesVoiceLibraryMarkers,
        setSeriesVoiceLibraryMarkers,
        roles,
        setRoles,
        seriesSpeakers,
        setSeriesSpeakers,
        editingRoleKey,
        setEditingRoleKey,
        editingRoleName,
        setEditingRoleName,
        isSavingRole,
        isFetchingSeriesSpeakers,
        isPromotingSpeaker,
        highlightedSubtitleId,
        setHighlightedSubtitleId,
        sourcesText,
        setSourcesText,
        targetText,
        setTargetText,
        targetTextForDemo,
        lastSubtitlesHash,
        // Refs
        textListRef,
        speakersFetchSeqRef,
        // Functions
        handleUpdateVolume,
        handleUpdateSpeed,
        handleOpenHistoryPanel,
        handleCloseHistoryPanel,
        extractSpeakersFromSubtitles,
        fetchSpeakersList,
        fetchSeriesSpeakersList,
        handlePromoteToSeriesSpeaker,
        handleDeleteSubtitle,
        handleAddSubtitle,
        handleSubtitleUpdate,
        handleGetNextSubtitleId,
        handleGetNextSubtitle,
        handleGetFullTimelineData,
        getLatestSubtitleData,
        buildSubtitleUpdateParams,
        handleSourceDataChange,
        handleUpdateVideoDataSpeed,
        handleTargetDataChange,
        handleRoleSelect,
        handleNewSpeaker,
        handleDeleteRole,
        handleStartEditRole,
        handleSaveRoleEdit,
        handleCancelRoleEdit,
        scrollToHighlightedSubtitle,
        updateTargetSubtitleHighlight,
        handleRefreshSubtitles,
    };
};
